import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import classNames from 'classnames';
import CurrencySymbol from '@/shared/components/CurrencySymbol';
import Checkbox from '@/shared/components/input/Checkbox';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import enumCache from '@/shared/services/EnumCacheService';
import utils from '@/shared/utils/adminpulse';
import messages from '../invoiceStreamMessages';

export default class UpdateInvoiceStreamAmountModal extends Component {
  state = {
    updateMethods: [],
    roundingMethods: [],
    selectedUpdateMethod: 0,
    amountOrPercentage: 0,
    selectedRoundingMethod: 0,
    roundingDecimals: 2,
    overrideManualChanges: false,
  };
  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    exampleAmount: PropTypes.number,
  };

  static defaultProps = { exampleAmount: utils.randomAmount() };

  componentDidMount() {
    const { intl } = this.props;
    Promise.all([
      enumCache.getTranslated('updateAmountMethod', intl),
      enumCache.getTranslated('roundingMethod', intl),
    ]).then(([updateMethods, roundingMethods]) => {
      this.setState({
        updateMethods,
        roundingMethods,
      });
    });
  }

  isFormInvalid = () => {
    const { amountOrPercentage, selectedUpdateMethod } = this.state;

    if (!amountOrPercentage || parseFloat(amountOrPercentage) === 0) {
      return true;
    }
    if (selectedUpdateMethod === 0 && (amountOrPercentage > 100 || amountOrPercentage < -100)) {
      return true;
    }
    return false;
  };

  amountChanged = value => {
    const { selectedUpdateMethod } = this.state;

    let parsedValue = Number.parseFloat(value);
    if (Number.isNaN(parsedValue)) {
      parsedValue = null;
    } else if (selectedUpdateMethod === 0) {
      // percentage: dont allow bigger/smaller than 100/-100
      parsedValue = Math.min(100, Math.max(-100, parsedValue));
    }

    this.setState(state => ({
      ...state,
      touched: true,
      amountOrPercentage: parsedValue,
    }));
  };

  onSave = () => {
    const {
      amountOrPercentage,
      overrideManualChanges,
      selectedUpdateMethod,
      selectedRoundingMethod,
      roundingDecimals,
    } = this.state;

    const { onSave } = this.props;

    this.setState({ touched: true });

    const amountUpdate = {
      method: selectedUpdateMethod,
      amountOrPercentage,
      overrideManualChanges,
      rounding: selectedRoundingMethod,
      roundingDecimals,
    };

    return onSave(amountUpdate);
  };
  generateExample = () => {
    const { exampleAmount } = this.props;
    const {
      amountOrPercentage,
      selectedUpdateMethod,
      selectedRoundingMethod,
      roundingDecimals,
    } = this.state;

    return utils.calculateIndexedAmount(
      Number.parseFloat(exampleAmount),
      selectedUpdateMethod,
      amountOrPercentage,
      selectedRoundingMethod,
      roundingDecimals,
    );
  };

  render() {
    const { show, onClose, intl, exampleAmount } = this.props;
    const {
      updateMethods,
      amountOrPercentage,
      touched,
      overrideManualChanges,
      selectedUpdateMethod,
      selectedRoundingMethod,
      roundingDecimals,
      roundingMethods,
    } = this.state;

    if (!show) return null;

    const amountClassName = classNames('form-group', {
      'has-error': touched && (!amountOrPercentage || parseFloat(amountOrPercentage) === 0),
    });
    const generatedAmount = this.generateExample();

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.UpdateAmount)}
        show={show}
        onClose={onClose}
        disabled={this.isFormInvalid()}
        onSave={() => this.onSave()}
      >
        <div className="form-horizontal">
          <div className="form-group">
            <label className="control-label col-sm-4">{intl.formatMessage(messages.Method)}</label>
            <div className="col-sm-5">
              {updateMethods && (
                <select
                  onChange={e => this.setState({ selectedUpdateMethod: parseInt(e.target.value) })}
                  className="form-control"
                  value={selectedUpdateMethod}
                >
                  {updateMethods.map(schedule => (
                    <option key={schedule.id} value={schedule.id}>
                      {schedule.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className={amountClassName}>
            <label className="control-label col-sm-4">
              {selectedUpdateMethod === 0
                ? intl.formatMessage(messages.Percentage)
                : intl.formatMessage(messages.Amount)}
            </label>
            <div className="col-sm-5">
              {(selectedUpdateMethod === 1 || selectedUpdateMethod === 2) && (
                <div className="input-group">
                  <input
                    type="number"
                    step="1"
                    value={amountOrPercentage}
                    onChange={e => this.amountChanged(e.target.value)}
                    className="form-control"
                  />
                  <span className="input-group-addon">
                    <CurrencySymbol />
                  </span>
                </div>
              )}
              {selectedUpdateMethod === 0 && (
                <div className="input-group">
                  <input
                    type="number"
                    step="1"
                    min="-100"
                    max="100"
                    value={amountOrPercentage}
                    onChange={e => this.amountChanged(e.target.value)}
                    className="form-control"
                  />
                  <span className="input-group-addon">%</span>
                </div>
              )}
            </div>
          </div>
          {selectedUpdateMethod === 0 && (
            <div className="form-group">
              <label className="control-label col-sm-4">
                {intl.formatMessage(messages.RoundingMethod)}
              </label>
              <div className="col-sm-5">
                {roundingMethods && (
                  <select
                    onChange={e =>
                      this.setState({ selectedRoundingMethod: parseInt(e.target.value) })
                    }
                    className="form-control"
                    value={selectedRoundingMethod}
                  >
                    {roundingMethods.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}
          {selectedUpdateMethod === 0 && (
            <div className="form-group">
              <label className="control-label col-sm-4">
                {intl.formatMessage(messages.RoundTo)}
              </label>
              <div className="col-sm-5">
                <div className="input-group">
                  <input
                    type="number"
                    onChange={e => this.setState({ roundingDecimals: parseInt(e.target.value) })}
                    className="form-control"
                    min={0}
                    max={5}
                    value={roundingDecimals}
                  />
                  <span className="input-group-addon">{intl.formatMessage(messages.Digits)}</span>
                </div>
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="control-label col-sm-4">{intl.formatMessage(messages.Example)}</label>
            <div className="col-sm-5">
              <div className="form-control-static inline-block">
                <FormattedCurrency value={exampleAmount} maximumFractionDigits={5} />
                <div className="text-center">&darr;</div>
                {!Number.isNaN(generatedAmount) && (
                  <FormattedCurrency value={generatedAmount} maximumFractionDigits={5} />
                )}
              </div>
            </div>
          </div>

          <div className="form-group">
            <Checkbox
              extraClass="col-sm-offset-4 col-md-8"
              id="overrideManualChanges"
              value={overrideManualChanges}
              onClick={() => this.setState({ overrideManualChanges: !overrideManualChanges })}
              text={intl.formatMessage(messages.OverrideManualInvoiceStreamScheduleChanges)}
            />
          </div>
          <div className="alert alert-warning" role="alert">
            <FormattedMessage {...messages.UpdateInvoiceStreamAmountWarning} />
          </div>
        </div>
      </OkCancelModal>
    );
  }
}
